// Fill command -- fills a layer (or selection) with a solid color,
// foreground, background, or a checkerboard pattern (transparent).

import type { Command } from "./Command";
import type { ActionStep } from "../actions/ActionStep";
import type { Document } from "../document/Document";
import type { RasterLayer } from "../document/Layer";
import { findRaster } from "../document/LayerUtils";

export type FillSource = "foreground" | "background" | "color" | "pattern" | "history";
export type FillBlendMode = "normal" | "dissolve" | "behind" | "clear";

export interface FillOptions {
  source: FillSource;
  color?: { r: number; g: number; b: number; a?: number };
  opacity: number;  // 0..1
  blendMode: FillBlendMode;
  /** If true, respects layer transparency lock. */
  respectLock: boolean;
}

export class FillCommand implements Command {
  readonly kind = "fill";

  private after?: Uint8ClampedArray;

  constructor(
    private readonly layerId: string,
    private readonly options: FillOptions,
    private readonly selectionMask?: Uint8Array, // null = fill entire layer
    private before?: Uint8ClampedArray,
  ) {}

  apply(doc: Document): Document {
    const layer = findRaster(doc.layers, this.layerId);
    if (!layer) return doc;

    const w = layer.pixels.width;
    const h = layer.pixels.height;
    const data = layer.pixels.data;

    // Capture the pre-fill snapshot so undo can restore it. (fillPixel mutates
    // `data` in place; 'dissolve' uses Math.random() and 'clear' reduces alpha
    // relative to the current value, so recomputing the fill on undo would NOT
    // revert — it would re-roll a new dither / compound the alpha down. Undo
    // must blit the captured snapshot back, not re-run apply().)
    if (!this.before) {
      this.before = new Uint8ClampedArray(data);
    }

    // Determine fill color
    let r = 0, g = 0, b = 0, a = 255;
    if (this.options.source === "foreground" && doc) {
      // Use current foreground -- resolved at apply time
      const fg = (doc as any)._fgColor;
      if (fg) { r = fg.r; g = fg.g; b = fg.b; a = fg.a ?? 255; }
    } else if (this.options.source === "background") {
      const bg = (doc as any)._bgColor;
      if (bg) { r = bg.r; g = bg.g; b = bg.b; a = bg.a ?? 255; }
    } else if (this.options.source === "color" && this.options.color) {
      r = this.options.color.r;
      g = this.options.color.g;
      b = this.options.color.b;
      a = this.options.color.a ?? 255;
    }

    const fillOpacity = this.options.opacity;
    const mask = this.selectionMask;

    for (let py = 0; py < h; py++) {
      for (let px = 0; px < w; px++) {
        const idx = (py * w + px) * 4;

        // If selection mask provided, only fill where mask > 0
        if (mask) {
          const maskVal = mask[py * w + px] ?? 0;
          if (maskVal === 0) continue;
          // Use mask alpha as fill opacity multiplier
          const ma = maskVal / 255;
          const blendedOpacity = fillOpacity * ma;
          this.fillPixel(data, idx, r, g, b, a, blendedOpacity);
        } else {
          this.fillPixel(data, idx, r, g, b, a, fillOpacity);
        }
      }
    }

    layer.pixels.markDirty();
    // Capture the post-fill snapshot so redo restores the exact filled result
    // (deterministic even for the random 'dissolve' mode).
    this.after = new Uint8ClampedArray(data);
    return doc;
  }

  invert(_before: Document, _after: Document): Command {
    // Undo = blit the pre-fill snapshot straight back (NOT re-run the fill).
    return this.makeRestore(this.before!, this.after!);
  }

  /** A restore command that writes `target` into the layer buffer; its own
   *  invert flips to `opposite`, so undo↔redo toggle between the pre- and
   *  post-fill snapshots without ever recomputing the (possibly random) fill. */
  private makeRestore(target: Uint8ClampedArray | undefined, opposite: Uint8ClampedArray | undefined): Command {
    const layerId = this.layerId;
    const self = this;
    return {
      kind: "fill-restore",
      apply(doc: Document): Document {
        if (!target) return doc;
        const layer = findRaster(doc.layers, layerId);
        if (!layer) return doc;
        layer.pixels.data.set(target);
        layer.pixels.markDirty();
        return doc;
      },
      invert(): Command {
        return self.makeRestore(opposite, target);
      },
    };
  }

  toActionStep(): ActionStep {
    return {
      id: crypto.randomUUID(),
      name: "Fill",
      command: "fill",
      params: { ...this.options } as Record<string, unknown>,
      recordable: true,
    };
  }

  private fillPixel(
    data: Uint8ClampedArray,
    idx: number,
    fr: number, fg: number, fb: number, fa: number,
    opacity: number,
  ): void {
    const dstR = data[idx]!;
    const dstG = data[idx + 1]!;
    const dstB = data[idx + 2]!;
    const dstA = data[idx + 3]!;
    const blendMode = this.options.blendMode;

    // Clear: force the pixel's alpha to 0 (scaled by the effective opacity /
    // selection-mask feathering so partial selections clear proportionally).
    if (blendMode === "clear") {
      const a = opacity;
      data[idx + 3] = Math.round(dstA * (1 - a));
      return;
    }

    // Behind: only paint into fully-transparent destination pixels, like
    // Photoshop's "Behind" paint mode -- never overwrites existing pixels.
    if (blendMode === "behind" && dstA !== 0) {
      return;
    }

    let effectiveOpacity = opacity;
    if (blendMode === "dissolve") {
      // Dissolve: per-pixel random dither. Each pixel is either painted at
      // full strength or left completely untouched, with the probability of
      // being painted equal to the effective opacity (including any
      // selection-mask feathering baked into `opacity`).
      if (Math.random() >= opacity) return;
      effectiveOpacity = 1;
    }

    const a = (fa / 255) * effectiveOpacity;
    data[idx] = Math.round(dstR * (1 - a) + fr * a);
    data[idx + 1] = Math.round(dstG * (1 - a) + fg * a);
    data[idx + 2] = Math.round(dstB * (1 - a) + fb * a);
    data[idx + 3] = Math.round(dstA * (1 - a) + 255 * a);
  }
}
